import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil, map, distinctUntilChanged } from 'rxjs/operators';
import { StorageService } from '../services/storage.service';
import { environment } from "src/environments/environment";

// Interfaces for type safety
interface ClubDetails {
  user_id: number; // Change from string to number
  club_id: number;
  club_name: string;
  jwttoken: string;
  multiClubs: string;
  club_subscription: boolean;
  membership_new_members: boolean;
  membership_form_completed: boolean;
  ready_steady?: string;
  sidebar?: any[];
  joined?: string;
  new_members_date?: string;
  colors: {
    primary: string;
    primary_rgb: string;
    secondary: string;
    secondary_rgb: string;
    tertiary?: string;
    tertiary_rgb?: string;
    status_bar: string;
  };
}

interface UserDetails {
  user_id: string;
  club_id: string;
  jwttoken: string;
  multiClubs: string;
  avatar?: string;
}

interface UnreadNotification {
  route: string;
  unread: boolean;
  type?: string;
  timestamp?: number;
}

interface ClubTheme {
  primary: string;
  primaryRgb: string;
  secondary: string;
  secondaryRgb: string;
  tertiary: string;
  tertiaryRgb: string;
}

interface CMCDetails {
  id: number;
  name: string;
  readySteady: string;
  subscription: boolean;
  statusBarColor: string;
  statusBarText: string;
}

// Constants
const USER_CONSTANTS = {
  DEFAULT_CLUB_NAME: 'connectMyClub',
  DEFAULT_READY_STEADY: 'Ready, steady...',
  DEFAULT_STATUS_BAR_COLOR: '#FF6000',
  DEFAULT_STATUS_BAR_TEXT: 'dark',
  MEMBERSHIP_ALLOWED_URLS: [
    "/app/tabs/my-details-account",
    "/app/tabs/my-details-personal", 
    "/app/tabs/my-details-ice",
    "/app/tabs/code-of-conduct",
    "/app/tabs/user-settings",
    "/app/tabs/about-licences",
    "/app/tabs/about-terms",
    "/app/tabs/about-privacy",
    "/app/tabs/switch-clubs"
  ]
} as const;

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  // Modern dependency injection
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);

  // API configuration
  public readonly config = {
    apiURL: environment.apiURL,
    siteUrl: environment.siteUrl,
    stripeKey: environment.stripeKey,
    mapsApiKey: environment.googleMapsApiKey
  };

  // User state
  private clubDetails: ClubDetails | null = null;
  public userDetails: UserDetails = {
    user_id: '',
    club_id: '',
    jwttoken: '',
    multiClubs: '0'
  };

  // Club data
  private readonly defaultCMCDetails: CMCDetails = {
    id: 0,
    name: USER_CONSTANTS.DEFAULT_CLUB_NAME,
    readySteady: USER_CONSTANTS.DEFAULT_READY_STEADY,
    subscription: false,
    statusBarColor: USER_CONSTANTS.DEFAULT_STATUS_BAR_COLOR,
    statusBarText: USER_CONSTANTS.DEFAULT_STATUS_BAR_TEXT
  };

  // Subscription management
  private readonly destroy$ = new Subject<void>();

  // Core observables - organized by category
  // Authentication & Permissions
  public readonly isAdmin = new BehaviorSubject<boolean>(false);
  public readonly canTimeEvents = new BehaviorSubject<boolean>(false);
  public readonly hasAssociations = new BehaviorSubject<boolean>(false);
  public readonly isRunLeader = new BehaviorSubject<boolean>(false);

  // App State
  public readonly isUpdateAvailable = new BehaviorSubject<boolean>(false);
  public readonly showMembershipForm = new BehaviorSubject<boolean>(false);
  public readonly inAppSessions = new BehaviorSubject<boolean>(false);
  public readonly showUserPinInput = new BehaviorSubject<boolean>(false);

  // Club Information
  public readonly clubID = new BehaviorSubject<number>(0);
  public readonly clubID$ = this.clubID.asObservable();
  public readonly clubName = new BehaviorSubject<string>(USER_CONSTANTS.DEFAULT_CLUB_NAME);
  public readonly clubName$ = this.clubName.asObservable();
  public readonly clubSubscription = new BehaviorSubject<boolean>(false);
  public readonly multiClubMember = new BehaviorSubject<boolean>(false);
  public readonly multiClubMember$ = this.multiClubMember.asObservable();
  public readonly clubDetailsSubscription = new BehaviorSubject<CMCDetails[]>([this.defaultCMCDetails]);

  // UI Components
  public readonly sideBarMenu = new BehaviorSubject<any[]>(['blank']);
  public readonly readySteady = new BehaviorSubject<string | null>(null);
  public readonly readySteady$ = this.readySteady.asObservable();
  public readonly avatar = new BehaviorSubject<string | null>(null);
  public readonly avatar$ = this.avatar.asObservable();
  public readonly clubLogo = new BehaviorSubject<string | null>(null);
  public readonly clubLogo$ = this.clubLogo.asObservable();

  // Notifications & Communications
  public readonly newAnnouncement = new BehaviorSubject<boolean>(false);
  public readonly newMessageBoard = new BehaviorSubject<boolean>(false);
  public readonly newDM = new BehaviorSubject<any>(false);
  public readonly newGroupMsg = new BehaviorSubject<boolean>(false);
  public readonly lastCommsCheck = new BehaviorSubject<boolean>(false);
  public readonly newOffer = new BehaviorSubject<boolean>(false);
  public readonly newComp = new BehaviorSubject<boolean>(false);
  public readonly unreadNotifications = new BehaviorSubject<UnreadNotification[]>([]);
  public readonly unreadNotifications$ = this.unreadNotifications.asObservable();

  // Updates & Events
  public readonly basketUpdated = new BehaviorSubject<boolean>(false);
  public readonly walletUpdated = new BehaviorSubject<boolean>(false);
  public readonly trainingWalletLive = new BehaviorSubject<boolean>(false);
  public readonly userChanged = new BehaviorSubject<boolean>(false);
  public readonly groupCancelled = new BehaviorSubject<any>(null);
  public readonly eventsCount = new BehaviorSubject<number>(0);
  public readonly eventsCount$ = this.eventsCount.asObservable();

  // Message Board & Training Updates
  public readonly messageBoardCommentUpdate = new BehaviorSubject<number[]>([]);
  public readonly trainingGroupUpdate = new BehaviorSubject<number | null>(null);
  public readonly trainingGroupLocationUpdate = new BehaviorSubject<any[]>([]);

  // Special Features
  public readonly streetHarassmentLocation = new BehaviorSubject<any>(null);
  public readonly streetHarassmentLocation$ = this.streetHarassmentLocation.asObservable();
  public readonly videoID = new BehaviorSubject<any>(null);
  public readonly videoID$ = this.videoID.asObservable();

  // Private observables
  private readonly newRoute = new BehaviorSubject<boolean>(false);

  // Computed observables
  public readonly hasUnreadNotifications$ = this.unreadNotifications$.pipe(
    map(notifications => notifications?.some(n => n.unread) || false),
    distinctUntilChanged()
  );

  public readonly clubInfo$ = combineLatest([
    this.clubName$,
    this.clubID$,
    this.readySteady$
  ]).pipe(
    map(([name, id, readySteady]) => ({
      name,
      id,
      readySteady: readySteady || USER_CONSTANTS.DEFAULT_READY_STEADY
    }))
  );

  constructor() {
    console.log('🚀 UserService initialized');
  }

  // Theme management
  public setThemeColors(theme: ClubTheme): void {
    try {
      const root = document.documentElement;
      const { primary, primaryRgb, secondary, secondaryRgb } = theme;
      
      // Validate and fallback for tertiary colors
      let { tertiary, tertiaryRgb } = theme;
      if (!tertiary || !this.isValidHexColor(tertiary)) {
        tertiary = primary;
        tertiaryRgb = primaryRgb;
      }

      // Set CSS custom properties
      const colorMappings = [
        ['--ion-color-primary', primary],
        ['--ion-color-primary-rgb', primaryRgb],
        ['--ion-color-primary-contrast', secondary],
        ['--ion-color-primary-contrast-rgb', secondaryRgb],
        ['--ion-color-primary-shade', primary],
        ['--ion-color-primary-tint', primary],
        ['--ion-color-secondary', secondary],
        ['--ion-color-secondary-rgb', secondaryRgb],
        ['--ion-color-secondary-contrast', tertiary],
        ['--ion-color-secondary-contrast-rgb', tertiaryRgb],
        ['--ion-color-secondary-shade', secondary],
        ['--ion-color-secondary-tint', secondary],
        ['--ion-color-tertiary', tertiary],
        ['--ion-color-tertiary-rgb', tertiaryRgb],
        ['--ion-color-tertiary-contrast', secondary],
        ['--ion-color-tertiary-contrast-rgb', secondaryRgb],
        ['--ion-color-tertiary-shade', tertiary],
        ['--ion-color-tertiary-tint', tertiary]
      ];

      colorMappings.forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });

      console.log('✅ Theme colors applied successfully');
    } catch (error) {
      console.error('❌ Error setting theme colors:', error);
    }
  }

  private isValidHexColor(hex: string): boolean {
    return /^#([0-9A-F]{3}){1,2}$/i.test(hex);
  }

  // Notification management
  public updateUnreadNotifications(route: string, unread: boolean = false): void {
    try {
      const current = this.unreadNotifications.getValue() || [];
      const existingIndex = current.findIndex(n => n.route == route);

      if (existingIndex > -1) {
        // Update existing notification
        current[existingIndex] = {
          ...current[existingIndex],
          unread,
          timestamp: Date.now()
        };
      } else {
        // Add new notification
        current.push({
          route,
          unread,
          timestamp: Date.now()
        });
      }

      this.unreadNotifications.next([...current]);
      console.log(`📬 Updated notification for route: ${route}, unread: ${unread}`);
    } catch (error) {
      console.error('❌ Error updating unread notifications:', error);
    }
  }

  public markAllNotificationsAsRead(): void {
    const current = this.unreadNotifications.getValue() || [];
    const updated = current.map(notification => ({
      ...notification,
      unread: false,
      timestamp: Date.now()
    }));
    
    this.unreadNotifications.next(updated);
    console.log('📬 All notifications marked as read');
  }

  public clearNotifications(): void {
    this.unreadNotifications.next([]);
    console.log('🗑️ All notifications cleared');
  }

  // Run leader status management
  public updateRunLeaderStatus(isLeader: boolean): void {
    this.isRunLeader.next(isLeader);
    console.log(`👨‍💼 Run leader status updated: ${isLeader}`);
  }

  public getRunLeaderStatus(): Observable<boolean> {
    return this.isRunLeader.asObservable();
  }

  // Route management
  public newRouteAdded(hasNewRoute: boolean): void {
    this.newRoute.next(hasNewRoute);
    console.log(`🛤️ New route status: ${hasNewRoute}`);
  }

  public getNewRouteAdded(): Observable<boolean> {
    return this.newRoute.asObservable();
  }

  // User data management
  public async getUser(): Promise<ClubDetails | null> {
    try {
      console.log('👤 Fetching user data...');

      // Check multi-club status first
      await this.checkMultiClubStatus();

      // Get club details from storage
      const clubDetailsData = await this.storage.getObject('clubDetails');
      console.log('📦 Fetched clubDetails from storage:', clubDetailsData);
      console.log('🔑 Val:', clubDetailsData);

      if (!clubDetailsData) {
        console.warn('⚠️ No club details found in storage');
        return null;
      }

      // Extract wrapped value if storage returns { value: ... } or use the object directly
      const clubObj: ClubDetails = (clubDetailsData && (clubDetailsData as any).value)
        ? (clubDetailsData as any).value as ClubDetails
        : (clubDetailsData as unknown as ClubDetails);

      // Basic validation to avoid assigning an incorrect shape
      if (!clubObj || typeof clubObj.user_id == 'undefined' || typeof clubObj.club_id == 'undefined') {
        console.warn('⚠️ Invalid club details structure', clubObj);
        return null;
      }

      this.clubDetails = clubObj;
      
      // Process club details
      await this.processClubDetails(this.clubDetails);
      
      console.log('✅ User data loaded successfully');
      return this.clubDetails;

    } catch (error) {
      console.error('❌ Error getting user data:', error);
      return null;
    }
  }

  private async checkMultiClubStatus(): Promise<void> {
    try {
      const multiClubsData = await this.storage.getString('multiClubs');
      const isMultiClub = multiClubsData?.value == '1';
      this.multiClubMember.next(isMultiClub);
      console.log(`🏢 Multi-club status: ${isMultiClub}`);
    } catch (error) {
      console.error('❌ Error checking multi-club status:', error);
      this.multiClubMember.next(false);
    }
  }

  private async processClubDetails(clubDetails: ClubDetails): Promise<void> {
    try {
      // Set theme colors
      this.setClubTheme(clubDetails);

      // Update user details
      this.updateUserDetails(clubDetails);

      // Update club information
      this.updateClubInformation(clubDetails);

      // Set up sidebar menu
      this.setupSidebarMenu(clubDetails);

      // Update club details subscription
      this.updateClubDetailsSubscription(clubDetails);

      // Handle membership form if needed
      await this.handleMembershipForm(clubDetails);

    } catch (error) {
      console.error('❌ Error processing club details:', error);
      throw error;
    }
  }

  private setClubTheme(clubDetails: ClubDetails): void {
    const colors = clubDetails.colors;
    if (!colors) {
      console.warn('⚠️ No color configuration found');
      return;
    }

    const theme: ClubTheme = {
      primary: colors.primary,
      primaryRgb: colors.primary_rgb,
      secondary: colors.secondary,
      secondaryRgb: colors.secondary_rgb,
      tertiary: colors.tertiary || colors.primary,
      tertiaryRgb: colors.tertiary_rgb || colors.primary_rgb
    };

    this.setThemeColors(theme);
  }

  private updateUserDetails(clubDetails: ClubDetails): void {
    const timestamp = Date.now();
    const userIdString = clubDetails.user_id.toString(); // Convert number to string
    const avatarUrl = `${this.config.apiURL}avatars/${userIdString}.png?${timestamp}`;

    this.userDetails = {
      user_id: userIdString, // Convert to string for userDetails
      club_id: clubDetails.club_id.toString(),
      jwttoken: clubDetails.jwttoken,
      multiClubs: clubDetails.multiClubs,
      avatar: avatarUrl
    };

    // Update observables
    this.avatar.next(avatarUrl);
    this.clubSubscription.next(clubDetails.club_subscription);

    // Store JWT token
    this.storage.setString('jwttoken', clubDetails.jwttoken);
    
    // Store user_id as string
    this.storage.setString('user_id', userIdString);
    
    console.log('✅ User details updated:', this.userDetails);
  }

  private updateClubInformation(clubDetails: ClubDetails): void {
    this.clubID.next(clubDetails.club_id);
    this.clubName.next(clubDetails.club_name);
    
    const clubLogoUrl = `${this.config.siteUrl}club_logo/${clubDetails.club_id}`;
    this.clubLogo.next(clubLogoUrl);

    const readySteady = clubDetails.ready_steady || USER_CONSTANTS.DEFAULT_READY_STEADY;
    this.readySteady.next(readySteady);

    console.log('CLUB DETAILS ',clubDetails)

   if(clubDetails['member'] && clubDetails['member']['admin']){ 
       let isAdmin = clubDetails['member']['admin']['is_admin'] == 1 ? true : false;
        this.isAdmin.next(isAdmin);
      }
    
    
  }

  private setupSidebarMenu(clubDetails: ClubDetails): void {
    const sidebar = clubDetails.sidebar || [];
    this.sideBarMenu.next(sidebar);
  }

  private updateClubDetailsSubscription(clubDetails: ClubDetails): void {
    const cmcDetails: CMCDetails = {
      id: clubDetails.club_id,
      name: clubDetails.club_name,
      readySteady: clubDetails.ready_steady || USER_CONSTANTS.DEFAULT_READY_STEADY,
      subscription: clubDetails.club_subscription,
      statusBarColor: clubDetails.colors?.primary || USER_CONSTANTS.DEFAULT_STATUS_BAR_COLOR,
      statusBarText: clubDetails.colors?.status_bar || USER_CONSTANTS.DEFAULT_STATUS_BAR_TEXT
    };

    this.clubDetailsSubscription.next([cmcDetails]);
  }

  private async handleMembershipForm(clubDetails: ClubDetails): Promise<void> {

    console.log('📝 Handling membership form logic ',clubDetails);
    if (!clubDetails.membership_new_members || clubDetails.membership_form_completed) {
      this.showMembershipForm.next(false);
      return;
    }

    this.showMembershipForm.next(true);
    
    const currentURL = this.router.url;
    const allowedURLs: string[] = [...USER_CONSTANTS.MEMBERSHIP_ALLOWED_URLS];

    // Allow club subscription pages
    if (currentURL.includes("club-subscription")) {
      allowedURLs.push(currentURL);
    }

    console.log('🔍 Current URL:', currentURL);
    console.log('✅ Allowed URLs:', allowedURLs);

    if (!allowedURLs.includes(currentURL)) {
      console.log('🔄 Redirecting to membership form');
      this.router.navigate(['app/tabs/membership'], { replaceUrl: true });
    }
  }

  // Utility methods
  public resetUserState(): void {
    console.log('🔄 Resetting user state');
    
    this.clubDetails = null;
    this.userDetails = {
      user_id: '',
      club_id: '',
      jwttoken: '',
      multiClubs: '0'
    };

    // Reset all observables to default values
    this.isAdmin.next(false);
    this.canTimeEvents.next(false);
    this.hasAssociations.next(false);
    this.isRunLeader.next(false);
    this.showMembershipForm.next(false);
    this.clubID.next(0);
    this.clubName.next(USER_CONSTANTS.DEFAULT_CLUB_NAME);
    this.clubSubscription.next(false);
    this.multiClubMember.next(false);
    this.avatar.next(null);
    this.clubLogo.next(null);
    this.readySteady.next(null);
    this.sideBarMenu.next(['blank']);
    this.unreadNotifications.next([]);
    this.clubDetailsSubscription.next([this.defaultCMCDetails]);
  }

  public getUserId(): string {
    return this.userDetails.user_id;
  }

  public getClubId(): string {
    return this.userDetails.club_id;
  }

  public getJwtToken(): string {
    return this.userDetails.jwttoken;
  }

  public isMultiClubMember(): boolean {
    return this.userDetails.multiClubs == '1';
  }

  ngOnDestroy(): void {
    console.log('🧹 UserService destroyed');
    
    this.destroy$.next();
    this.destroy$.complete();

    // Complete all BehaviorSubjects
    const subjects = [
      this.isAdmin, this.canTimeEvents, this.hasAssociations, this.isRunLeader,
      this.isUpdateAvailable, this.showMembershipForm, this.inAppSessions,
      this.showUserPinInput, this.clubID, this.clubName, this.clubSubscription,
      this.multiClubMember, this.clubDetailsSubscription, this.sideBarMenu,
      this.readySteady, this.avatar, this.clubLogo, this.newAnnouncement,
      this.newMessageBoard, this.newDM, this.newGroupMsg, this.lastCommsCheck,
      this.newOffer, this.newComp, this.unreadNotifications, this.basketUpdated,
      this.walletUpdated, this.trainingWalletLive, this.userChanged,
      this.groupCancelled, this.eventsCount, this.messageBoardCommentUpdate,
      this.trainingGroupUpdate, this.trainingGroupLocationUpdate,
      this.streetHarassmentLocation, this.videoID, this.newRoute
    ];

    subjects.forEach(subject => subject.complete());
    
    console.log('✅ UserService cleanup complete');
  }

  // New method to get app details
  public async getAppDetails(): Promise<void> {
    // Get user ID and ensure it's a string
    if (!this.userDetails.user_id) {
      const userIdFromService = this.getUserId();
      this.userDetails.user_id = userIdFromService || '';

      // Try to get from storage if still empty
      if (!this.userDetails.user_id) {
        const userIdData = await this.storage.getString('user_id');
        if (userIdData?.value) {
          this.userDetails.user_id = userIdData.value;
        }
      }
    }

    console.log('📊 App details status - JWT:', !!this.getJwtToken(), 'UserID:', this.userDetails.user_id, 'ClubID:', this.userDetails.club_id);
  }
}

